// packages/storefront/src/utils/widgetLanguage.ts
//
// What a host page's `data-lang` attribute says about an embed widget's language — the one reading of it for every
// widget entry (the shop, the chat launcher, the reviews widget; apps/embed/AGENTS.md). A code TourTask does not
// support is a setup mistake of the page, and the widget says so (`WidgetLanguageAlert`): it never starts in a
// language it cannot load, which the API refuses and the widget used to report as a connection error, and never
// silently in English.

import type { AppLocale } from '@tourtask/types/i18n';
import { languageTagLocale } from '@tourtask/utils/shared/i18n/languageTagLocale';

/** Why a widget has no language to run in. */
export type WidgetLanguageProblem =
	/** The host page sets no `data-lang` (or an empty one). */
	| { kind: 'absent' }
	/** `data-lang` holds a code that names no supported language: `given` is the attribute as written. */
	| { kind: 'unsupported'; given: string };

export type WidgetLanguage = { kind: 'supported'; locale: AppLocale } | WidgetLanguageProblem;

/**
 * Reads `data-lang` (`null` when the attribute is absent). A language tag is read by its primary subtag in any
 * case (`es-CL`, `ES` → `es`; `languageTagLocale`), so the region a page adds never makes its code unsupported.
 * What each widget does with an absent attribute is its own rule.
 */
export function readWidgetLanguage(dataLang: string | null): WidgetLanguage {
	const given = (dataLang ?? '').trim();
	if (given === '') return { kind: 'absent' };
	const locale = languageTagLocale(given);
	return locale === null ? { kind: 'unsupported', given } : { kind: 'supported', locale };
}
